import { useState } from 'react';
import { Box, MantineTheme, Title, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useGetForm, usePatchFormResetSlug } from 'api/forms';
import {
  ConfirmationDialogModal,
  InputWithCopyButton,
} from 'components/shared';
import { LinkIcon } from 'icons';
import type { Form } from 'types';

import { generateFormIframe, generateFormUrl } from './helpers';

type Props = {
  form: Form;
  disableSlugReset?: boolean;
};

const getTitleSx = (theme: MantineTheme) => ({
  fontSize: theme.fontSizes.lg,
  lineHeight: 1.5,
  marginBottom: theme.other.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.other.spacing(1),
});

export const FormSharingOptions = ({
  form: { id, slug },
  disableSlugReset = false,
}: Props) => {
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const { mutateAsync: resetSlug } = usePatchFormResetSlug({ id });
  const { refetch: refetchForm } = useGetForm({ id });
  const { t } = useTranslation('forms');

  const onResetSlugConfirm = async () => {
    await resetSlug();
    await refetchForm();
    setConfirmationDialogOpen(false);
  };

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        flexDirection: 'column',
        gap: theme.other.spacing(2),
      })}
    >
      <Box>
        <Title sx={getTitleSx} order={6}>
          <LinkIcon />
          {t('common.formSharingOptions.url.title')}
        </Title>
        <InputWithCopyButton value={generateFormUrl(slug)} />
        {!disableSlugReset && (
          <UnstyledButton
            sx={theme => ({
              marginTop: theme.other.spacing(1),
              color: theme.colors.violet[0],
              fontWeight: 600,
            })}
            onClick={() => setConfirmationDialogOpen(true)}
          >
            {t('common.formSharingOptions.url.generateNewButton')}
          </UnstyledButton>
        )}
        <ConfirmationDialogModal
          title={t('common.formSharingOptions.url.confirmationDialog.title')}
          message={t(
            'common.formSharingOptions.url.confirmationDialog.message',
          )}
          confirmButtonText={t(
            'common.formSharingOptions.url.confirmationDialog.confirmButton',
          )}
          variant="danger"
          opened={confirmationDialogOpen}
          onClose={() => setConfirmationDialogOpen(false)}
          onConfirm={onResetSlugConfirm}
        />
      </Box>
      <Box>
        <Title sx={getTitleSx} order={6}>
          {t('common.formSharingOptions.iframe.title')}
        </Title>
        <InputWithCopyButton value={generateFormIframe(slug)} />
      </Box>
    </Box>
  );
};
